// LearnCore Frontend - 首页
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpenIcon, 
  UserGroupIcon, 
  SparklesIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

export default function HomePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              智能学习，从
              <span className="text-primary-600"> LearnCore </span>
              开始
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              基于用户生成内容的学习平台，创建、分享和学习知识卡片。
              让学习变得更高效、更有趣。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="btn-primary text-lg px-8 py-3"
                  >
                    <BookOpenIcon className="h-5 w-5 mr-2" />
                    进入学习空间
                  </Link>
                  <Link
                    to="/sets/create"
                    className="btn-outline text-lg px-8 py-3"
                  >
                    <SparklesIcon className="h-5 w-5 mr-2" />
                    创建学习集
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="btn-primary text-lg px-8 py-3"
                  >
                    <PlayIcon className="h-5 w-5 mr-2" />
                    开始学习
                  </Link>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Link
                      to="/login"
                      className="btn-outline text-lg px-8 py-3"
                    >
                      家长登录
                      <ArrowRightIcon className="h-5 w-5 ml-2" />
                    </Link>
                    <Link
                      to="/child/login"
                      className="btn-outline text-lg px-8 py-3 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                    >
                      孩子登录
                      <ArrowRightIcon className="h-5 w-5 ml-2" />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              为什么选择 LearnCore？
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              我们提供最简单、最高效的学习工具，帮助您快速掌握知识。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
                <BookOpenIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                抽认卡学习
              </h3>
              <p className="text-gray-600">
                经典的抽认卡模式，通过反复练习加深记忆，提高学习效率。
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success-100 rounded-full mb-6">
                <UserGroupIcon className="h-8 w-8 text-success-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                分享协作
              </h3>
              <p className="text-gray-600">
                轻松分享学习集，与同学和朋友一起学习，共同进步。
              </p>
            </div>

            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-warning-100 rounded-full mb-6">
                <SparklesIcon className="h-8 w-8 text-warning-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                简单易用
              </h3>
              <p className="text-gray-600">
                直观的界面设计，快速创建和管理学习内容，专注于学习本身。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              如何使用 LearnCore？
            </h2>
            <p className="text-xl text-gray-600">
              只需三个简单步骤，开始您的学习之旅
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-600 text-white rounded-full font-bold text-lg mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                创建学习集
              </h3>
              <p className="text-gray-600">
                添加术语和定义，创建属于您的学习集。支持公开分享或私人学习。
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-600 text-white rounded-full font-bold text-lg mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                开始学习
              </h3>
              <p className="text-gray-600">
                使用抽认卡模式进行学习，点击卡片查看答案，加深记忆。
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-600 text-white rounded-full font-bold text-lg mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                分享交流
              </h3>
              <p className="text-gray-600">
                通过链接分享学习集，与他人一起学习，构建学习社区。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            准备开始学习了吗？
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            加入 LearnCore，体验全新的学习方式。免费注册，立即开始。
          </p>
          
          {!user && (
            <Link
              to="/register"
              className="inline-flex items-center bg-white text-primary-600 font-semibold px-8 py-3 rounded-md hover:bg-gray-50 transition-colors text-lg"
            >
              <PlayIcon className="h-5 w-5 mr-2" />
              免费开始学习
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
